import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAccessControl } from "@/hooks/useAccessControl";
import {
  BarChart3,
  Bot,
  Box,
  BookOpen,
  Folder,
  MapPin,
  ArrowLeftRight,
  Users,
  Truck,
  Briefcase,
  Navigation,
  CheckSquare,
  MessageSquare,
  Upload,
  Shield,
  LayoutDashboard,
  FileSpreadsheet,
  Images,
  CalendarDays,
  CalendarRange,
  Presentation,
  Columns3,
  CircleUserRound,
  FolderKanban,
  TrendingUp,
  UserPlus,
  ShoppingBag,
  BookMarked,
  Activity,
  LineChart,
  Building2,
  ListChecks,
  Landmark,
  Code2,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Megaphone,
  Mail,
  FileText,
  Instagram,
  Newspaper,
  ScrollText,
} from "lucide-react";

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface NavItem {
  to: string;
  icon: React.ElementType;
  labelKey: string;
  requiredPermissions?: string[];
}

const navGroups: NavGroup[] = [
  {
    label: "nav.group.overview",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, labelKey: "nav.dashboard" },
      { to: "/agenda", icon: CalendarDays, labelKey: "nav.agenda" },
      { to: "/operations", icon: Columns3, labelKey: "nav.operations" },
      { to: "/projects", icon: FolderKanban, labelKey: "nav.projects" },
    ],
  },
  {
    label: "nav.group.catalog",
    items: [
      { to: "/artworks", icon: Box, labelKey: "nav.artworks" },
      { to: "/listas", icon: ListChecks, labelKey: "nav.artworkLists" },
      { to: "/artists", icon: CircleUserRound, labelKey: "nav.artists" },
      { to: "/programs", icon: Presentation, labelKey: "nav.programs" },
      { to: "/media-assets", icon: Images, labelKey: "nav.mediaAssets" },
      { to: "/items", icon: Box, labelKey: "nav.items" },
      { to: "/categories", icon: Folder, labelKey: "nav.categories" },
      { to: "/locations", icon: MapPin, labelKey: "nav.locations" },
    ],
  },
  {
    label: "nav.group.stock",
    items: [
      { to: "/stock", icon: BarChart3, labelKey: "nav.stock" },
      { to: "/movements", icon: ArrowLeftRight, labelKey: "nav.movements" },
    ],
  },
  {
    label: "nav.group.comercial",
    items: [
      { to: "/gerencial", icon: LineChart, labelKey: "nav.gerencial" },
      { to: "/recipients", icon: Users, labelKey: "nav.recipients" },
      { to: "/destinatarios/listas", icon: ListChecks, labelKey: "nav.recipient_lists" },
      { to: "/conversations", icon: MessageSquare, labelKey: "nav.conversations" },
      { to: "/pipeline", icon: TrendingUp, labelKey: "nav.pipeline" },
      { to: "/leads", icon: UserPlus, labelKey: "nav.leads" },
      { to: "/pedidos", icon: ShoppingBag, labelKey: "nav.pedidos" },
      { to: "/catalogs", icon: BookOpen, labelKey: "nav.catalogs" },
      { to: "/catalogs-pipeline", icon: BookMarked, labelKey: "nav.catalogsPipeline" },
      { to: "/catalogs-tracking", icon: Activity, labelKey: "nav.catalogsTracking" },
      { to: "/shipments", icon: Truck, labelKey: "nav.shipments" },
    ],
  },
  {
    label: "nav.group.campaigns",
    items: [
      { to: "/marketing/campanhas-email", icon: Mail, labelKey: "nav.campanhasEmail" },
      { to: "/kits", icon: Briefcase, labelKey: "nav.kits" },
      { to: "/offline-actions", icon: Navigation, labelKey: "nav.offlineActions" },
    ],
  },
  {
    label: "nav.group.comunicacao",
    items: [
      { to: "/comunicacao", icon: LayoutDashboard, labelKey: "nav.comunicacaoDashboard", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/calendario", icon: CalendarDays, labelKey: "nav.comunicacaoCalendario", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/calendario-editorial", icon: CalendarRange, labelKey: "nav.comunicacaoCalendarioEditorial", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/kanban", icon: Columns3, labelKey: "nav.comunicacaoKanban", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/campanhas", icon: Megaphone, labelKey: "nav.comunicacaoCampanhas", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/conteudos", icon: FileText, labelKey: "nav.comunicacaoConteudos", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/aprovacoes", icon: CheckSquare, labelKey: "nav.comunicacaoAprovacoes", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/assets", icon: Images, labelKey: "nav.comunicacaoAssets", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/metricas", icon: BarChart3, labelKey: "nav.comunicacaoMetricas", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/simulacao", icon: Instagram, labelKey: "nav.comunicacaoSimulacao", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/contatos-imprensa", icon: Newspaper, labelKey: "nav.comunicacaoContatosImprensa", requiredPermissions: ["comunicacao"] },
      { to: "/comunicacao/press-releases", icon: ScrollText, labelKey: "nav.comunicacaoPressReleases", requiredPermissions: ["comunicacao"] },
    ],
  },
  {
    label: "nav.group.financeiro",
    items: [
      { to: "/financeiro", icon: Wallet, labelKey: "nav.financeiroVisaoGeral", requiredPermissions: ["financeiro", "diretoria"] },
      { to: "/financeiro/contas-receber", icon: ArrowDownCircle, labelKey: "nav.financeiroReceber", requiredPermissions: ["financeiro", "diretoria"] },
      { to: "/financeiro/contas-pagar", icon: ArrowUpCircle, labelKey: "nav.financeiroPagar", requiredPermissions: ["financeiro", "diretoria"] },
      { to: "/financeiro/pagamentos-artistas", icon: Users, labelKey: "nav.financeiroRepasses", requiredPermissions: ["financeiro", "diretoria"] },
      { to: "/financeiro/conciliacao", icon: ArrowLeftRight, labelKey: "nav.financeiroConciliacao", requiredPermissions: ["financeiro", "diretoria"] },
      { to: "/financeiro/cadastros", icon: Landmark, labelKey: "nav.financeiroCadastros", requiredPermissions: ["financeiro", "diretoria"] },
      { to: "/financeiro/desenvolvimento", icon: Code2, labelKey: "nav.financeiroDevSistema", requiredPermissions: ["financeiro", "diretoria"] },
    ],
  },
  {
    label: "nav.group.reports",
    items: [
      { to: "/relatorios", icon: FileSpreadsheet, labelKey: "nav.reports", requiredPermissions: ["financeiro", "diretoria"] },
    ],
  },
  {
    label: "nav.group.admin",
    items: [
      { to: "/audit", icon: CheckSquare, labelKey: "nav.audit" },
      { to: "/imports", icon: Upload, labelKey: "nav.imports" },
      { to: "/users", icon: Shield, labelKey: "nav.users" },
      { to: "/ai-assistants", icon: Bot, labelKey: "nav.aiAssistants" },
      { to: "/gallery-config", icon: Building2, labelKey: "nav.galleryConfig" },
    ],
  },
];

export function Sidebar({
  open = false,
  onNavigate,
}: {
  /** Gaveta aberta — só tem efeito abaixo de md; no desktop a sidebar é estática. */
  open?: boolean;
  /** Chamado ao tocar num item: fecha a gaveta no celular. */
  onNavigate?: () => void;
} = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin, permissions } = useAccessControl();

  const canAccessItem = (item: NavItem): boolean => {
    if (isAdmin) return true;
    if (!item.requiredPermissions || item.requiredPermissions.length === 0) return true;
    return item.requiredPermissions.some((p) => permissions.includes(p));
  };

  const visibleGroups = navGroups
    .filter((group) => group.label !== "nav.group.admin" || isAdmin)
    .map((group) => ({ ...group, items: group.items.filter(canAccessItem) }))
    .filter((group) => group.items.length > 0);

  return (
    <aside
      className={[
        "flex flex-col w-sidebar bg-gl-black shrink-0 overflow-hidden",
        // Celular: gaveta deslizante, fora do fluxo. Antes a sidebar era estatica
        // e comia 240px de uma tela de 375px — sobravam ~71px de conteudo.
        "fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out",
        open ? "translate-x-0" : "-translate-x-full",
        // Desktop (>=768px): volta a ser coluna estatica e sempre visivel.
        // Estas classes md: reproduzem exatamente o comportamento anterior.
        "md:static md:translate-x-0 md:transition-none md:min-h-screen",
      ].join(" ")}
    >
      {/* Logo */}
      <div className="px-6 py-6 border-b border-white/10">
        <img
          src="/assets/logos/logo-white.svg"
          alt="Galeria Leme"
          className="h-[34px] object-contain object-left"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="text-[10px] text-white/25 mt-2 tracking-[0.25em] uppercase font-medium">
          {t("app.subtitle")}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white px-3 pt-4 pb-1 font-bold first:pt-2">
              {t(group.label)}
            </div>
            {group.items.map(({ to, icon: Icon, labelKey }) => {
              const isActive =
                location.pathname === to ||
                (to !== "/dashboard" && location.pathname.startsWith(to + "/"));
              return (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onNavigate}
                  className={[
                    "relative flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors",
                    "tracking-[0.01em]",
                    isActive
                      ? "sidebar-link-active bg-[rgba(217,237,9,0.1)] text-white"
                      : "text-white/[0.55] hover:bg-[rgba(217,237,9,0.08)] hover:text-white/90",
                  ].join(" ")}
                >
                  <Icon
                    className={[
                      "w-4 h-4 shrink-0 transition-opacity",
                      isActive ? "opacity-100" : "opacity-50",
                    ].join(" ")}
                  />
                  <span>{t(labelKey)}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-1.5 h-1.5 rounded-full bg-gl-accent inline-block" />
          <span className="text-[10px] text-white/30 tracking-wide">
            {t("app.systemOnline")}
          </span>
        </div>
        <div className="text-[10px] text-white/20 tracking-wide">
          v1.0.0 &middot; {t("app.internalUse")}
        </div>
      </div>
    </aside>
  );
}
